'use client';

import { Fragment, type ReactNode } from 'react';
import { cn } from '@/app/utils';

interface StudioTabsProps<T extends string> {
  value: T;
  /** dividerAfter：在这个 tab 后面加一根竖线，用来分组 */
  options: readonly {
    value: T;
    label: ReactNode;
    icon?: ReactNode;
    accessory?: ReactNode;
    dividerAfter?: boolean;
  }[];
  onChange: (value: T) => void;
  /** tab 之后、竖线分隔的附加操作（如「上传角色」） */
  extra?: ReactNode;
  /** 最右侧的内容（如视图切换） */
  trailing?: ReactNode;
  className?: string;
}

/** 三个步骤共用的普通 tab 栏：与资产页一致，选中粗线压在底部细线上 */
export function StudioTabs<T extends string>({
  value,
  options,
  onChange,
  extra,
  trailing,
  className,
}: StudioTabsProps<T>) {
  return (
    <div
      className={cn('mb-2 flex min-w-0 items-center justify-between gap-4 border-system-white08 border-b', className)}
    >
      <div
        data-studio-tabs-scroll
        className="flex min-w-0 flex-1 touch-pan-x items-center gap-5 overflow-x-auto overscroll-x-contain [scrollbar-width:none] sm:gap-3 [&::-webkit-scrollbar]:hidden"
      >
        {options.map((option) => (
          <Fragment key={option.value}>
            <span className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                aria-pressed={value === option.value}
                onClick={() => onChange(option.value)}
                className={cn(
                  'relative flex shrink-0 items-center gap-1.5 whitespace-nowrap py-3 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01',
                  value === option.value ? 'font-medium text-white' : 'text-system-white64 hover:text-system-white80',
                )}
              >
                {option.icon}
                {option.label}
                {value === option.value ? (
                  <span className="-bottom-px absolute left-0 h-[2px] w-full rounded-full bg-white" />
                ) : null}
              </button>
              {option.accessory}
            </span>
            {option.dividerAfter ? <span aria-hidden="true" className="h-3.5 w-px shrink-0 bg-system-white16" /> : null}
          </Fragment>
        ))}
        {extra ? (
          <>
            <span aria-hidden="true" className="h-3.5 w-px shrink-0 bg-system-white16" />
            {extra}
          </>
        ) : null}
      </div>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </div>
  );
}
